import axios from "axios";
import { diagFromAxios, setAuthDiag } from "./lib/diagnostics";

const BASE = process.env.REACT_APP_BACKEND_URL;
export const API = `${BASE}/api`;

// Session token persistence — Safari on iOS silently drops the httpOnly cross-domain cookie set
// by /auth/session even with SameSite=None; Secure (ITP behaviour). To survive Safari we also
// stash the token in localStorage and attach it as Authorization: Bearer on every request.
// The backend `_get_current_user` reads EITHER the cookie OR the header.
const TOKEN_KEY = "meyetasks_session_token";
export const saveSessionToken = (token) => {
    try {
        if (token) localStorage.setItem(TOKEN_KEY, token);
        else localStorage.removeItem(TOKEN_KEY);
    } catch (_) { /* private mode / disabled storage — best effort */ }
};
export const getSessionToken = () => {
    try { return localStorage.getItem(TOKEN_KEY) || null; } catch (_) { return null; }
};

// `withCredentials` sends the httpOnly `session_token` cookie back to the API on every request
// so auth-gated endpoints get their user context. On iOS Safari the cookie may be dropped, so
// we ALSO send the token in the Authorization header from localStorage as a redundant path.
export const http = axios.create({ baseURL: API, withCredentials: true });
const clientTimeZone = (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone || ""; } catch { return ""; } })();
http.interceptors.request.use((config) => {
    const tok = getSessionToken();
    if (tok) config.headers.Authorization = `Bearer ${tok}`;
    // IANA zone (e.g. America/Vancouver) so server-side date guards use the user's local calendar day, DST included.
    if (clientTimeZone) config.headers["X-Client-TZ"] = clientTimeZone;
    return config;
});

// ONE place reacts to "the server no longer knows you": any 401 from a data endpoint (not the
// auth endpoints themselves) re-verifies the session via /auth/me and, if that fails too, drops
// the user to the login screen instead of leaving a signed-in-looking UI whose every write fails.
let unauthorizedHandler = null;
let lastUnauthorizedAt = 0;
export const onUnauthorized = (fn) => { unauthorizedHandler = fn; };
http.interceptors.response.use(
    (r) => { diagFromAxios(r.config, r.status); return r; },
    (err) => {
        const status = err?.response?.status;
        const url = err?.config?.url || "";
        diagFromAxios(err?.config, status ?? "network", { message: String(err?.response?.data?.detail?.code || err?.response?.data?.detail || err?.message || "").slice(0, 120) });
        if (status === 401) setAuthDiag({ last401At: new Date().toISOString() });
        if (status === 401 && !url.startsWith("/auth/") && unauthorizedHandler && Date.now() - lastUnauthorizedAt > 5000) {
            lastUnauthorizedAt = Date.now();
            try { unauthorizedHandler(err); } catch (_) { /* never mask the original error */ }
        }
        return Promise.reject(err);
    },
);

const newRequestId = () =>
    (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

export const api = {
    // tasks
    // tz offset lets the server heal legacy recurring snoozes on the user's REAL calendar date.
    listTasks: () => http.get("/tasks", { params: { tz_offset_min: new Date().getTimezoneOffset() } }).then((r) => r.data),
    // Every create carries a fresh client_request_id — the backend treats (user, id) as permanently
    // idempotent, so a retried/duplicated request can never insert a second record.
    // client_request_id is minted ONCE at the logical submit boundary (caller passes it) so a retry
    // reuses the same key; only when a caller forgets do we mint one here.
    createTask: (data) => http.post("/tasks", { client_request_id: data?.client_request_id || newRequestId(), ...data }).then((r) => r.data),
    newRequestId,
    // Atomic per-occurrence writes — SOURCE iso only; backend validates identity.
    // TEMPORARY tracing: `X-Trace-Id` ties the click → request → task_events row → SSE echo together.
    completeOccurrence: (id, sourceIso, traceId = newRequestId()) => http.post(`/tasks/${id}/occurrences/${sourceIso}/complete`, null, { headers: { "X-Trace-Id": traceId } }).then((r) => r.data),
    reopenOccurrence: (id, sourceIso, traceId = newRequestId()) => http.delete(`/tasks/${id}/occurrences/${sourceIso}/complete`, { headers: { "X-Trace-Id": traceId } }).then((r) => r.data),
    taskTrace: (id, iso) => http.get(`/tasks/${id}/trace`, { params: { iso: iso || undefined, tz_offset_min: new Date().getTimezoneOffset() } }).then((r) => r.data),
    newTraceId: () => newRequestId(),
    snoozeOccurrence: (id, sourceIso, { minutes, until } = {}) => http.post(`/tasks/${id}/occurrences/${sourceIso}/snooze`, { minutes, until }).then((r) => r.data),
    unsnoozeOccurrence: (id, sourceIso) => http.delete(`/tasks/${id}/occurrences/${sourceIso}/snooze`).then((r) => r.data),
    restoreOverrides: (id) => http.post(`/tasks/${id}/restore-overrides`).then((r) => r.data),
    taskDuplicates: () => http.get("/tasks/duplicates").then((r) => r.data),
    taskSplitCandidates: () => http.get("/tasks/split-candidates").then((r) => r.data),
    applyTaskSplits: (items) => http.post("/tasks/split", { items }).then((r) => r.data),
    unsplitTask: (id) => http.post(`/tasks/${id}/unsplit`).then((r) => r.data),
    repairOverrideChains: (ids = null) => http.post(`/tasks/repair-override-chains`, ids ? { ids } : {}, { params: { dry_run: !ids } }).then((r) => r.data),
    updateTask: (id, data, traceId = null) => http.patch(`/tasks/${id}`, data, traceId ? { headers: { "X-Trace-Id": traceId } } : undefined).then((r) => r.data),
    deleteTask: (id) => http.delete(`/tasks/${id}`).then((r) => r.data),
    snoozeTask: (id, untilMs) => http.post(`/tasks/${id}/snooze`, { until: untilMs }).then((r) => r.data),
    /** Recurring only — snooze specific beats (`isos`) until `untilMs`. Writes `occurrence_snoozes`. */
    snoozeOccurrencesBulk: (id, sourceIsos, untilMs) => http.post(`/tasks/${id}/occurrences/snooze-bulk`, { source_isos: sourceIsos, until: untilMs }).then((r) => r.data),
    /** One-tap heal for the legacy `type=recurring, interval=null` rows the old AI produced.
     *  Server converts them to `scheduled` (if dated) or `todo` (if not). Idempotent. */
    repairNullRecurring: () => http.post("/tasks/repair-null-recurring").then((r) => r.data),
    postponeTask: (id, fromDate, toDate, toTime, reason = "postpone") => http.post(`/tasks/${id}/postpone`, { from_date: fromDate, to_date: toDate, to_time: toTime || null, reason }).then((r) => r.data),
    unpostponeTask: (id, fromDate) => http.post(`/tasks/${id}/unpostpone`, { from_date: fromDate }).then((r) => r.data),

    // notes
    listNotes: () => http.get("/notes").then((r) => r.data),
    createNote: (data) => http.post("/notes", data).then((r) => r.data),
    updateNote: (id, data) => http.patch(`/notes/${id}`, data).then((r) => r.data),
    deleteNote: (id) => http.delete(`/notes/${id}`).then((r) => r.data),

    // checklists
    listChecklists: () => http.get("/checklists").then((r) => r.data),
    createChecklist: (data) => http.post("/checklists", data).then((r) => r.data),
    updateChecklist: (id, data) => http.patch(`/checklists/${id}`, data).then((r) => r.data),
    deleteChecklist: (id) => http.delete(`/checklists/${id}`).then((r) => r.data),
    convertChecklistToInventory: (id) =>
        http.post(`/checklists/${id}/convert-to-inventory`).then((r) => r.data),

    // scratchpads
    listScratchpads: () => http.get("/scratchpads").then((r) => r.data),
    createScratchpad: (data) => http.post("/scratchpads", data).then((r) => r.data),
    updateScratchpad: (id, data) => http.patch(`/scratchpads/${id}`, data).then((r) => r.data),
    deleteScratchpad: (id) => http.delete(`/scratchpads/${id}`).then((r) => r.data),

    // glossary
    listGlossary: () => http.get("/glossary").then((r) => r.data),
    createGlossary: (data) => http.post("/glossary", data).then((r) => r.data),
    updateGlossary: (id, data) => http.patch(`/glossary/${id}`, data).then((r) => r.data),
    deleteGlossary: (id) => http.delete(`/glossary/${id}`).then((r) => r.data),
    autoCategorizeGlossary: (force = false) => http.post(`/glossary/auto-categorize?force=${force}`).then((r) => r.data),
    suggestGlossaryLinks: () => http.post("/glossary/suggest-links").then((r) => r.data),

    // weather
    getWeather: (opts = {}) => {
        const params = new URLSearchParams();
        if (opts.lat != null) params.set("lat", opts.lat);
        if (opts.lon != null) params.set("lon", opts.lon);
        if (opts.days) params.set("days", opts.days);
        const q = params.toString();
        return http.get(`/weather${q ? "?" + q : ""}`).then((r) => r.data);
    },

    // inventory
    listInventory: () => http.get("/inventory").then((r) => r.data),
    createInventory: (data) => http.post("/inventory", data).then((r) => r.data),
    updateInventory: (id, data) => http.patch(`/inventory/${id}`, data).then((r) => r.data),
    deleteInventory: (id) => http.delete(`/inventory/${id}`).then((r) => r.data),

    // bookmarks (Chrome extension smart-capture + in-app URL bar)
    listBookmarks: () => http.get("/bookmarks").then((r) => r.data),
    createBookmark: (data) => http.post("/bookmarks", data).then((r) => r.data),
    updateBookmark: (id, data) => http.patch(`/bookmarks/${id}`, data).then((r) => r.data),
    deleteBookmark: (id) => http.delete(`/bookmarks/${id}`).then((r) => r.data),
    /** Smart-capture: send URL (and optional page text) → LLM classifies + extracts + saves.
     *  Returns { bookmark, reused } — `reused` = the user already had this URL saved. */
    extractBookmark: (payload) => http.post("/bookmarks/extract", payload).then((r) => r.data),
    /** Promote a saved bookmark to a permanent glossary entry. Idempotent. */
    promoteBookmark: (id) => http.post(`/bookmarks/${id}/promote-to-glossary`).then((r) => r.data),

    // attachments
    listAttachments: () => http.get("/attachments").then((r) => r.data),
    createAttachment: (data) => http.post("/attachments", data).then((r) => r.data),
    deleteAttachment: (id) => http.delete(`/attachments/${id}`).then((r) => r.data),

    // settings
    getSettings: () => http.get("/settings").then((r) => r.data),
    updateSettings: (data) => http.patch("/settings", data).then((r) => r.data),

    // ai
    aiProcess: (text, chat_log = [], current_city = null) =>
        http
            .post("/ai/process", {
                text,
                chat_log,
                current_city,
                client_request_id: newRequestId(),
                tz_offset_min: new Date().getTimezoneOffset(),
            })
            .then((r) => r.data),
    aiMission: (text, chat_log = [], kind = "user", silent = false, requestId = null) =>
        http
            .post("/ai/mission", {
                text,
                chat_log,
                kind,
                silent,
                client_request_id: requestId || newRequestId(),
                // Send the browser's timezone offset so the backend computes the user's LOCAL date/time.
                // Otherwise the AI reports Sunday's tasks late Saturday PST when the server clock has
                // already ticked past midnight UTC.
                tz_offset_min: new Date().getTimezoneOffset(),
            })
            .then((r) => r.data),

    // Semantic search — fallback when the literal query has few/no hits.
    semanticSearch: (query) =>
        http.post("/search/semantic", { query }).then((r) => r.data),

    // geofence
    geofence: (city) => http.post("/geofence", { city }).then((r) => r.data),

    // backup
    exportBackup: () => http.get("/backup/export").then((r) => r.data),
    importBackup: (data, { apply = false } = {}) => http.post("/backup/import", data, { params: apply ? { apply: true } : {} }).then((r) => r.data),

    // transcribe
    transcribe: (blob) => {
        const fd = new FormData();
        const filename = blob.type && blob.type.includes("webm") ? "audio.webm" : "audio.wav";
        fd.append("file", blob, filename);
        return http
            .post("/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } })
            .then((r) => r.data);
    },
};

export default api;
